import { useNavigate } from "@tanstack/react-router";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/errors";
import { useAuth } from "@/shared/auth";
import { toast } from "@/shared/lib/toast";

import { useLogin } from "../api/use-login";
import { LoginForm } from "../components/LoginForm";
import type { LoginFormValues } from "../lib/login-schema";

export function LoginPage(): JSX.Element {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { mutate, isPending, error } = useLogin();

  function handleSubmit(values: LoginFormValues): void {
    mutate(values, {
      onSuccess: (data) => {
        login(data.access_token, data.expires_at, data.user);
        void navigate({ to: "/dashboard" });
      },
      onError: (err) => {
        if (!(err instanceof ApiError && err.status === 401)) {
          toast.error(t("common:errors.unexpected"));
        }
      },
    });
  }

  const serverError =
    error instanceof ApiError && error.status === 401 ? t("auth:error.invalid") : undefined;

  return (
    <main
      className="flex min-h-screen items-center justify-center bg-[var(--surface-page)]"
      data-testid="login-page"
    >
      <div className="w-full max-w-sm rounded-xl border border-[var(--border-default)] bg-white p-8 shadow-[var(--sh-2)]">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-bold text-[var(--text-primary)]">{t("auth:title")}</h1>
          <p className="mt-1 text-sm text-[var(--text-subtle)]">{t("auth:subtitle")}</p>
        </div>
        <LoginForm
          onSubmit={handleSubmit}
          isLoading={isPending}
          {...(serverError ? { serverError } : {})}
        />
        <p className="mt-6 text-center text-xs text-[var(--text-subtle)]">
          {t("auth:security_notice")}
        </p>
      </div>
    </main>
  );
}
